import { Calendar, ClockUser, Monitor } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks";
import { useTeamMemberSessions } from "../../../hooks/get/useTeamMemberSessions";
import { useTeamMembers } from "../../../hooks/get/useTeamMembers";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../shared/constants";
import SessionFilter, { UserOption } from "../components/Filter";
import Sessions from "../components/Sessions";
import { ISessionActivityFilters, ISessionItem } from "../interface";
import { Tabs } from "flowbite-react";

export type TabType = "active" | "past";

export default function SessionActivityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<TabType>("active");

  const [filters, setFilters] = useState<ISessionActivityFilters>({
    user_id: user?.id ? String(user.id) : "",
    from_date: undefined,
    to_date: undefined,
  });

  // Build user dropdown from team members (first page, large per_page to reduce calls)
  const { data: teamMembersData, isLoading: membersLoading } = useTeamMembers({
    page: 1,
    per_page: DEFAULT_ITEMS_PER_PAGE * 5,
  });

  const userOptions: UserOption[] = useMemo(() => {
    const results = teamMembersData?.results || [];
    const options: UserOption[] = [
      {
        value: user?.id ? String(user.id) : "",
        label: `${user?.name || user?.email}(${t("common.me")})`,
      },
      ...results.map((m) => ({
        value: String(m.user?.id || m.id),
        label: m.user?.name || m.user?.email || m.email || "-",
      })),
    ];

    return options;
  }, [teamMembersData?.results, t, user?.id, user?.email, user?.name]);

  const selectedUserId = filters.user_id || (user?.id ? String(user.id) : "");

  const {
    data: sessionsResp,
    isLoading,
    refetch,
    isRefetching,
  } = useTeamMemberSessions({ user_id: selectedUserId }, !!selectedUserId);

  const filteredSessions: ISessionItem[] = useMemo(() => {
    let sessions = sessionsResp?.results || [];
    if (filters.from_date) {
      sessions = sessions.filter((s) =>
        dayjs(s.start_time).isAfter(dayjs(filters.from_date).startOf("day"))
      );
    }
    if (filters.to_date) {
      sessions = sessions.filter((s) =>
        dayjs(s.start_time).isBefore(dayjs(filters.to_date).endOf("day"))
      );
    }
    return sessions;
  }, [sessionsResp?.results, filters.from_date, filters.to_date]);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 pl-2">
          <ClockUser size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
          <h1 className="text-sm font-bold text-gray-700 dark:text-gray-300">
            {t("configurations.team.sessions.button")}
          </h1>
          <span>|</span>
          <Tabs
            theme={{
              base: "flex gap-2",
              tabpanel: "p-0",
            }}
            aria-label="Session tabs"
            style="pills"
            onActiveTabChange={(tab) =>
              setActiveTab(tab === 0 ? "active" : "past")
            }
          >
            <Tabs.Item
              active={activeTab === "active"}
              title={
                <div className="flex items-center gap-2">
                  <Monitor size={16} />
                  Active Sessions (
                  {filteredSessions.filter((s) => s.is_active).length})
                </div>
              }
            ></Tabs.Item>
            <Tabs.Item
              active={activeTab === "past"}
              title={
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  Past Sessions (
                  {filteredSessions.filter((s) => !s.is_active).length})
                </div>
              }
            />
          </Tabs>
        </div>
        <SessionFilter
          values={filters}
          onChange={setFilters}
          userOptions={userOptions}
          refetch={refetch}
          isRefetching={isRefetching}
        />
      </div>

      {/* Content - full screen, no scrollbar for the grid */}
      <div className="flex-1">
        <div className="h-full w-full ">
          <Sessions
            key={isRefetching.toString()}
            sessions={filteredSessions}
            isLoading={isLoading || membersLoading || isRefetching}
            activeTab={activeTab}
          />
        </div>
      </div>
    </div>
  );
}
